import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Plus, Settings, Users, Calendar, Image, Layers } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useProjectStore } from '../stores/projectStore'
import CreateProjectDialog from '../components/project/CreateProjectDialog'
import ProjectCard from '../components/project/ProjectCard'
import Header from '../components/layout/Header'

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { projects, loading, fetchProjects } = useProjectStore()
  const [showCreateProject, setShowCreateProject] = useState(false)

  useEffect(() => {
    if (user) {
      fetchProjects(user.uid)
    }
  }, [user, fetchProjects])

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-400 via-primary-600 to-primary-800">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-white/3 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/3 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          {/* Welcome Section */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome back, {user?.displayName || user?.email?.split('@')[0]}
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Continue your creative journey with visual storytelling
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <motion.div
                className="glass-card text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Layers className="w-8 h-8 text-primary-300 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{projects.length}</div>
                <div className="text-white/70 text-sm">Projects</div>
              </motion.div>
              
              <motion.div
                className="glass-card text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Image className="w-8 h-8 text-primary-300 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">
                  {projects.reduce((sum, p) => sum + p.totalImages, 0)}
                </div>
                <div className="text-white/70 text-sm">Images</div>
              </motion.div>
              
              <motion.div
                className="glass-card text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Calendar className="w-8 h-8 text-primary-300 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">
                  {projects.reduce((sum, p) => sum + p.totalScenes, 0)}
                </div>
                <div className="text-white/70 text-sm">Scenes</div>
              </motion.div>
              
              <motion.div
                className="glass-card text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <Users className="w-8 h-8 text-primary-300 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">
                  {projects.reduce((sum, p) => sum + p.sharedWith.length, 0)}
                </div>
                <div className="text-white/70 text-sm">Collaborators</div>
              </motion.div>
            </div>
          </motion.div>

          {/* Projects Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-white">Your Projects</h2>
              <button
                onClick={() => setShowCreateProject(true)}
                className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-semibold px-6 py-3 flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>New Project</span>
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="glass-card h-64 animate-pulse"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <div className="w-full h-32 bg-white/10 rounded-lg mb-4" />
                    <div className="h-4 bg-white/10 rounded mb-2" />
                    <div className="h-3 bg-white/5 rounded w-3/4" />
                  </motion.div>
                ))}
              </div>
            ) : projects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {projects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <ProjectCard
                      project={project}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                className="text-center py-24"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="glass-card max-w-md mx-auto">
                  <Layers className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    No projects yet
                  </h3>
                  <p className="text-white/70 mb-6">
                    Create your first project to start building beautiful storyboards
                  </p>
                  <button
                    onClick={() => setShowCreateProject(true)}
                    className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-semibold px-6 py-3 flex items-center space-x-2 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Create Project</span>
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateProject}
        onClose={() => setShowCreateProject(false)}
      />
    </div>
  )
}

export default Dashboard 